import type { GameEvent } from "./game-types";
import { usesDogPlaceholder } from "./fighter-config";

export class GameAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  unlock(): void {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = 0.22;
      this.master.connect(this.context.destination);
      this.noiseBuffer = this.createNoiseBuffer(this.context);
    }
    void this.context.resume();
  }

  handleEvent(event: GameEvent): void {
    if (!this.context || !this.master) {
      return;
    }
    if (event.type === "announce") {
      const frequency =
        event.text === "FIGHT" ? 148 : 220 + Number.parseInt(event.text, 10) * 52;
      this.tone(frequency, 0.14, "square", 0.23, -70);
      if (event.text === "FIGHT") {
        this.tone(296, 0.25, "sawtooth", 0.17, 120);
      }
      return;
    }
    if (event.type === "jump") {
      this.tone(usesDogPlaceholder(event.fighter) ? 330 : 260, 0.11, "sine", 0.1, 170);
      this.noise(0.08, 0.045, 900);
      return;
    }
    if (event.type === "attack") {
      this.noise(0.09, 0.075, 1300);
      this.tone(usesDogPlaceholder(event.fighter) ? 170 : 125, 0.08, "triangle", 0.08, -50);
      return;
    }
    if (event.type === "special") {
      this.tone(usesDogPlaceholder(event.fighter) ? 520 : 390, 0.3, "sine", 0.16, 310);
      this.tone(usesDogPlaceholder(event.fighter) ? 760 : 590, 0.18, "triangle", 0.08, -90);
      return;
    }
    if (event.type === "hit") {
      this.noise(0.12, Math.min(0.22, 0.1 + event.damage * 0.006), 420);
      this.tone(
        usesDogPlaceholder(event.attacker) ? 118 : 86,
        0.13,
        "square",
        Math.min(0.2, 0.09 + event.damage * 0.004),
        -45,
      );
      return;
    }
    if (event.type === "shield") {
      this.tone(usesDogPlaceholder(event.fighter) ? 610 : 470, 0.16, "sine", 0.11, -180);
      return;
    }
    if (event.type === "shield-break") {
      this.noise(0.42, 0.18, 1800);
      this.tone(110, 0.44, "sawtooth", 0.16, -80);
      return;
    }
    if (event.type === "grab") {
      this.tone(94, 0.12, "triangle", 0.11, -25);
      return;
    }
    if (event.type === "ledge-grab") {
      this.tone(
        usesDogPlaceholder(event.fighter) ? 420 : 340,
        0.13,
        "sine",
        0.09,
        120,
      );
      return;
    }
    if (event.type === "throw") {
      this.noise(0.16, event.forward ? 0.11 : 0.15, 520);
      this.tone(
        event.forward ? 132 : 108,
        0.2,
        "sawtooth",
        event.forward ? 0.12 : 0.16,
        -54,
      );
      return;
    }
    if (event.type === "ko") {
      this.noise(0.42, 0.2, 260);
      this.tone(82, 0.52, "sawtooth", 0.2, -55);
      return;
    }
    if (event.type === "respawn") {
      this.tone(260, 0.36, "sine", 0.11, 440);
      return;
    }
    if (event.type === "gameover") {
      this.tone(usesDogPlaceholder(event.winner) ? 220 : 164, 0.55, "triangle", 0.18, 220);
      window.setTimeout(() => {
        this.tone(usesDogPlaceholder(event.winner) ? 330 : 246, 0.7, "triangle", 0.14, 150);
      }, 120);
    }
  }

  private tone(
    frequency: number,
    duration: number,
    waveform: OscillatorType,
    volume: number,
    frequencyDelta: number,
  ): void {
    if (!this.context || !this.master) {
      return;
    }
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = waveform;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(30, frequency + frequencyDelta),
      now + duration,
    );
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private noise(duration: number, volume: number, cutoff: number): void {
    if (!this.context || !this.master || !this.noiseBuffer) {
      return;
    }
    const now = this.context.currentTime;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = this.noiseBuffer;
    filter.type = "lowpass";
    filter.frequency.value = cutoff;
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    source.start(now);
    source.stop(now + duration);
  }

  private createNoiseBuffer(context: AudioContext): AudioBuffer {
    const frameCount = Math.round(context.sampleRate * 0.8);
    const buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < frameCount; index += 1) {
      data[index] = Math.random() * 2 - 1;
    }
    return buffer;
  }
}
